import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    signal,
} from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';

import { ToolbarService } from './toolbar.service';
import { AuthService } from '@components/auth/auth.service';
import { CurrentUserService } from '@services/current-user.service';
import { CompanyApiService } from '@services/company-api.service';
import { ICompany } from '@interfaces/company.interface';
import { AdminCompanyContextService } from '@services/admin-company-context.service';
import { Role } from '@app/domain/models';
import { SurveysService } from '@data/surveys.service';
import { CategoriesService } from '@data/categories.service';
import { MatTooltip } from '@angular/material/tooltip';
import { ThemeService } from '@services/theme.service';

/**
 * AppShellComponent
 * -----------------
 * Единый тулбар для всего приложения + слот контента (router-outlet).
 * Бизнес-логика меню перенесена из Dashboard, чтобы тулбар был один общий.
 * По умолчанию тулбар показан везде, кроме страницы /login (чтобы не мешал авторизации).
 */
@Component({
    selector: 'app-shell',
    standalone: true,
    imports: [
        RouterOutlet,
        MatToolbarModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        MatFormFieldModule,
        MatInputModule,
        MatSnackBarModule,
        MatSelectModule,
        MatTooltip,
    ],
    templateUrl: './shell.component.html',
    styleUrl: './shell.component.scss',
})
export class AppShellComponent {
    protected authService = inject(AuthService);
    protected currentUserService = inject(CurrentUserService);
    protected adminCompanyContextService = inject(AdminCompanyContextService);
    protected readonly themeService = inject(ThemeService);
    private router = inject(Router);
    private companyApiService = inject(CompanyApiService);
    private surveysService = inject(SurveysService);
    private categoriesService = inject(CategoriesService);
    private destroyRef = inject(DestroyRef);
    readonly toolbar = inject(ToolbarService);

    private currentRoute = signal<string>(this.router.url);
    protected readonly isSidebarExpanded = signal(false);
    protected companies = signal<ICompany[]>([]);
    protected readonly navItems = [
        {
            icon: 'home',
            label: 'Главная',
            routePrefix: '/dashboard',
            action: () => this.goDashboard(),
        },
        {
            icon: 'assignment',
            label: 'Исследования',
            routePrefix: '/survey-list',
            action: () => this.goSurveys(),
        },
        {
            icon: 'insights',
            label: 'Результаты',
            routePrefix: '/results',
            action: () => this.goResults(),
        },
        {
            icon: 'groups',
            label: 'Респонденты',
            routePrefix: '/respondents',
            action: () => this.goRespondents(),
        },
        {
            icon: 'shopping_bag',
            label: 'Образцы',
            routePrefix: '/product-list',
            action: () => this.goProducts(),
        },
        {
            icon: 'category',
            label: 'Категории',
            routePrefix: '/categories-list',
            action: () => this.goCategories(),
        },
        {
            icon: 'group',
            label: 'Пользователи',
            routePrefix: '/users-list',
            action: () => this.goUsers(),
        },
        {
            icon: 'file_download',
            label: 'Выгрузка',
            routePrefix: '/download-report',
            action: () => this.goDownloadReport(),
        },
        {
            icon: 'menu_book',
            label: 'Инструкция',
            routePrefix: '/user-guide',
            action: () => this.goUserGuide(),
        },
    ];

    constructor() {
        if (this.shouldLoadShellUserData()) {
            this.currentUserService.load().subscribe();
        }

        effect(() => {
            if (!this.shouldLoadShellUserData()) {
                return;
            }
            if (this.currentUserService.user()?.role === Role.ADMIN) {
                this.loadAdminCompanies();
            }
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.currentRoute.set(event.url);
                    if (this.shouldLoadShellUserData()) {
                        this.currentUserService.load().subscribe();
                    }
                    this.updateToolbarTitle();
                }
            });
    }

    private updateToolbarTitle() {
        let route = this.router.routerState.root;

        while (route.firstChild) {
            route = route.firstChild;
        }
        const pageTitle = route.snapshot.data['title'];
        const baseTitle = 'Сенсорика';

        if (pageTitle) {
            this.toolbar.set({ title: `${baseTitle} ${pageTitle}` });
        } else {
            this.toolbar.set({ title: baseTitle });
        }
    }

    readonly isAuthRoute = computed(() => this.currentRoute() === '/login');
    readonly isSurveyRunRoute = computed(() => {
        const route = this.currentRoute();
        return route.startsWith('/survey/go/');
    });
    readonly isAdmin = computed(
        () => this.currentUserService.user()?.role === Role.ADMIN,
    );
    readonly titleSuffix = computed(() =>
        this.toolbar
            .title()
            .replace(/^Сенсорика(?:\s*·\s*)?/, '')
            .trim(),
    );
    readonly showSidebar = computed(
        () => !this.isAuthRoute() && !this.isSurveyRunRoute(),
    );

    toggleSidebar(): void {
        this.isSidebarExpanded.update((v) => !v);
    }

    isActiveRoute(routePrefix: string): boolean {
        return this.currentRoute().startsWith(routePrefix);
    }

    // --- Переходы и действия меню ---
    goUsers() {
        this.router.navigate(['/users-list']);
    }

    goTests() {
        this.router.navigate(['/tests-list']);
    }

    goSurveys() {
        this.router.navigate(['/survey-list']);
    }

    goProducts() {
        this.router.navigate(['/product-list']);
    }

    goCategories() {
        this.router.navigate(['/categories-list']);
    }

    goCompanies() {
        this.router.navigate(['/companies-list']);
    }

    goRespondents() {
        this.router.navigate(['/respondents']);
    }

    goDownloadReport() {
        this.router.navigate(['/download-report']);
    }

    goCreate() {
        this.router.navigate(['/create']);
    }

    goDashboard() {
        this.router.navigate(['/dashboard']);
    }

    goResults() {
        this.router.navigate(['/results']);
    }

    goUserGuide() {
        this.router.navigate(['/user-guide']);
    }

    logout() {
        this.authService.logout();
    }

    toggleTheme(): void {
        this.themeService.toggle();
    }

    onCompanyChange(companyId: number | string): void {
        this.adminCompanyContextService.setCompanyId(companyId);
        this.surveysService.clearCache();
        this.categoriesService.clearCache();

        this.categoriesService.reload().subscribe();
        this.surveysService.reload().subscribe();

        this.router.navigate(['/dashboard']);
    }

    private loadAdminCompanies(): void {
        this.companyApiService.getCompanies().subscribe({
            next: (companies) => {
                this.companies.set(companies);
                if (!companies.length) {
                    this.adminCompanyContextService.setCompanyId(null);
                    return;
                }

                const currentCompanyId =
                    this.adminCompanyContextService.companyId();
                const exists = currentCompanyId
                    ? companies.some(
                          (company) => company.id === currentCompanyId,
                      )
                    : false;

                if (!exists)
                    this.adminCompanyContextService.setCompanyId(
                        companies[0].id,
                    );
            },
            error: () => {
                this.companies.set([]);
            },
        });
    }

    private shouldLoadShellUserData(): boolean {
        const path =
            typeof window !== 'undefined'
                ? window.location.pathname
                : this.currentRoute();
        return !path.startsWith('/survey/go/');
    }
}
